/**
 * Stage 1 entry — before a run has risk items.
 *
 * End to end in one place: the default tab runs the release risk assessment
 * *inside* the run (pick the current and upcoming documents from Blob, one
 * click compares them and extracts the risk items). The second tab attaches an
 * assessment that already exists on the AI page — both land in the same place.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { IconInfoCircle, IconLink, IconPlayerPlay } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { BlobFilePicker, type BlobSelection } from "@/features/ai/BlobFilePicker";
import { listAssessments, type AssessmentOut } from "@/shared/api/ai";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import { attachAssessment, runAssessmentInRun, type RunOut } from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

/** Kept in step with `services/document_extract.py::SUPPORTED_EXTENSIONS`. */
const ACCEPTED = [
  ".pdf",
  ".docx",
  ".xlsx",
  ".xlsm",
  ".csv",
  ".tsv",
  ".txt",
  ".md",
  ".json",
  ".jsonl",
  ".xml",
  ".html",
] as const;

interface Props {
  run: RunOut;
  onUpdated: (run: RunOut, jump?: boolean) => void;
}

export function Stage1Start({ run, onUpdated }: Props) {
  const [tab, setTab] = useState<string | null>("run");

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <div>
          <Text fw={600}>Stage 1 — release risk assessment</Text>
          <Text size="sm" c="dimmed">
            Compare the current configuration against the upcoming release. The findings become the
            risk items you confirm — everything stays in this run.
          </Text>
        </div>
        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab value="run">Run assessment here</Tabs.Tab>
            <Tabs.Tab value="attach">Attach an existing one</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="run" pt="md">
            <RunHerePanel run={run} onUpdated={onUpdated} />
          </Tabs.Panel>
          <Tabs.Panel value="attach" pt="md">
            <AttachExistingPanel run={run} onUpdated={onUpdated} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Paper>
  );
}

function RunHerePanel({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const [blobConnections, setBlobConnections] = useState<ConnectionOut[] | null>(null);
  const [blobId, setBlobId] = useState<string | null>(run.blob_connection_id);
  const [current, setCurrent] = useState<BlobSelection[]>([]);
  const [upcoming, setUpcoming] = useState<BlobSelection[]>([]);
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const page = await listConnections(await getToken(), "blob");
      setBlobConnections(page.items);
      setBlobId((prev) => prev ?? page.items[0]?.connection_id ?? null);
    } catch (err) {
      toastApiError("Could not list Blob connections", err);
      setBlobConnections([]);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<BlobSelection[]>>,
    file: BlobSelection,
  ) =>
    setter((prev) =>
      prev.some((f) => f.container === file.container && f.path === file.path)
        ? prev.filter((f) => !(f.container === file.container && f.path === file.path))
        : [...prev, file],
    );

  const ready = Boolean(
    blobId && current.length > 0 && upcoming.length > 0 && run.is_owner && !running,
  );

  const onRun = async () => {
    if (!ready || !blobId) return;
    setRunning(true);
    setFailure(null);
    try {
      const fresh = await runAssessmentInRun(await getToken(), run.run_id, {
        blob_connection_id: blobId,
        current: current.map((f) => ({ container: f.container, path: f.path })),
        upcoming: upcoming.map((f) => ({ container: f.container, path: f.path })),
      });
      onUpdated(fresh);
      toastSuccess(
        "Assessment complete",
        `${fresh.risks?.length ?? 0} risk items extracted — confirm them below.`,
      );
    } catch (err) {
      setFailure(err instanceof Error ? err.message : "The assessment failed.");
      toastApiError("Assessment failed", err);
    } finally {
      setRunning(false);
    }
  };

  if (blobConnections !== null && blobConnections.length === 0) {
    return (
      <Alert variant="light" color="yellow" icon={<IconInfoCircle size={18} />}>
        This needs an Azure Blob connection holding the configuration documents. Add one under
        Integrations.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Select
        label="Blob connection"
        description="Where the configuration documents live"
        value={blobId}
        onChange={(next) => {
          setBlobId(next);
          setCurrent([]);
          setUpcoming([]);
        }}
        data={(blobConnections ?? []).map((c) => ({
          value: c.connection_id,
          label: c.display_name,
        }))}
        allowDeselect={false}
        maw={360}
      />
      {blobId && (
        <Group grow align="flex-start">
          <BlobFilePicker
            connectionId={blobId}
            label="Current configuration"
            value={null}
            onChange={() => undefined}
            values={current}
            onToggle={(f) => toggle(setCurrent, f)}
            acceptedExtensions={ACCEPTED}
          />
          <BlobFilePicker
            connectionId={blobId}
            label="Upcoming configuration / release notes"
            value={null}
            onChange={() => undefined}
            values={upcoming}
            onToggle={(f) => toggle(setUpcoming, f)}
            acceptedExtensions={ACCEPTED}
          />
        </Group>
      )}
      <Group justify="flex-end">
        <Button
          leftSection={<IconPlayerPlay size={16} />}
          disabled={!ready}
          loading={running}
          onClick={() => void onRun()}
        >
          Run assessment & extract risks
        </Button>
      </Group>
      {running && (
        <Text size="xs" c="dimmed" ta="right">
          Reading the documents, assessing the change, and extracting risk items — two model calls,
          this can take a minute.
        </Text>
      )}
      {failure && (
        <Alert variant="light" color="red" title="The assessment did not run">
          <Text size="sm">{failure}</Text>
        </Alert>
      )}
    </Stack>
  );
}

function AttachExistingPanel({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const [assessments, setAssessments] = useState<AssessmentOut[] | null>(null);
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const page = await listAssessments(await getToken());
      const ok = page.items.filter((a) => a.status === "succeeded");
      setAssessments(ok);
      setPick(ok[0]?.assessment_id ?? null);
    } catch (err) {
      toastApiError("Could not list assessments", err);
      setAssessments([]);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const attach = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const fresh = await attachAssessment(await getToken(), run.run_id, pick);
      onUpdated(fresh);
      toastSuccess("Assessment attached", `${fresh.risks?.length ?? 0} risk items extracted.`);
    } catch (err) {
      toastApiError("Could not attach the assessment", err);
    } finally {
      setBusy(false);
    }
  };

  if (assessments === null) {
    return (
      <Group justify="center" p="md">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        A comparison already run on the{" "}
        <Anchor component={RouterLink} to="/ai/risk-assessment" size="sm">
          AI → Release risk assessment
        </Anchor>{" "}
        page. Its findings are extracted the moment it is attached.
      </Text>
      <Group align="flex-end">
        <Select
          label="Completed assessments"
          data={assessments.map((a) => ({
            value: a.assessment_id,
            label: `${a.title} · ${new Date(a.created_at_utc).toLocaleDateString()}`,
          }))}
          value={pick}
          onChange={setPick}
          searchable
          style={{ flex: 1 }}
          disabled={!run.is_owner}
        />
        <Button
          leftSection={<IconLink size={16} />}
          disabled={!pick || !run.is_owner}
          loading={busy}
          onClick={() => void attach()}
        >
          Attach & extract risks
        </Button>
      </Group>
    </Stack>
  );
}
